import { computed, inject, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';
import { ChatAdapterFactory } from '@lb/api/mylb/services/chat-adapter.factory';
import { IChatMessage, IThread } from '@lb/api/mylb/interfaces';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';

export interface IChatState {
	threads: IThread[];
	activeThreadId: string | null;
	messages: IChatMessage[];
	loading: boolean;
	loaded: boolean;
}

export const initChatState: IChatState = {
	threads: [],
	activeThreadId: null,
	messages: [],
	loading: false,
	loaded: false
};

@Injectable({ providedIn: 'root' })
export class ChatStateService {
	private readonly _adapterFactory = inject(ChatAdapterFactory);
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);
	private _configured = false;

	private get _adapter() {
		// Configure on first access
		if (!this._configured) {
			const chatConfig = this._appSettings.chat;
			if (!chatConfig) {
				throw new Error('Chat configuration is not available in app settings');
			}
			this._adapterFactory.configure({
				backend: chatConfig.backend,
				n8nWebhookUrl: chatConfig.n8n?.webhookUrl || '',
				n8nChatInputKey: chatConfig.n8n?.chatInputKey || 'chatInput',
				n8nSessionKey: chatConfig.n8n?.sessionKey || 'sessionId',
				n8nIncludeAuthToken: chatConfig.n8n?.includeAuthToken ?? true,
				n8nAdditionalHeaders: chatConfig.n8n?.additionalHeaders || {}
			});
			this._configured = true;
		}
		return this._adapterFactory.getAdapter();
	}

	// State signals
	private _state = signal<IChatState>(initChatState);
	public isSlideoutOpen = signal<boolean>(false);

	// Selectors
	public threads = computed(() => this._state().threads);
	public activeThread = computed(() => {
		const state = this._state();
		return state.threads.find((t) => t.id === state.activeThreadId);
	});
	public activeThreadId = computed(() => this._state().activeThreadId);
	public messages = computed(() => this._state().messages);
	public loading = computed(() => this._state().loading);
	public loaded = computed(() => this._state().loaded);

	// Actions
	public openSlideout(): void {
		this.isSlideoutOpen.set(true);
		if (!this._state().loaded) {
			this.loadThreads();
		}
	}

	public closeSlideout(): void {
		this.isSlideoutOpen.set(false);
	}

	public toggleSlideout(): void {
		if (this.isSlideoutOpen()) {
			this.closeSlideout();
		} else {
			this.openSlideout();
		}
	}

	public loadThreads(): void {
		this._updateState({ loading: true });

		this._adapter
			.getThreads()
			.pipe(take(1))
			.subscribe({
				next: (threads) => {
					// Auto-create thread if none exist
					if (threads.length === 0) {
						this.createNewThread();
					} else {
						this._updateState({
							threads,
							loading: false,
							loaded: true
						});
						// Auto-select first thread
						if (threads.length > 0) {
							this.setActiveThread(threads[0].id);
						}
					}
				},
				error: (error) => {
					console.error('Error loading threads', error);
					this._updateState({ loading: false });
				}
			});
	}

	public createNewThread(): void {
		this._updateState({ loading: true });

		this._adapter
			.createThread()
			.pipe(take(1))
			.subscribe({
				next: (thread) => {
					const threads = [...this._state().threads, thread];
					this._updateState({
						threads,
						activeThreadId: thread.id,
						messages: [],
						loading: false,
						loaded: true
					});
				},
				error: (error) => {
					console.error('Error creating thread', error);
					this._updateState({ loading: false });
				}
			});
	}

	public setActiveThread(threadId: string): void {
		this._updateState({ activeThreadId: threadId });
		this.loadMessages(threadId);
	}

	public loadMessages(threadId: string): void {
		this._updateState({ loading: true });

		this._adapter
			.getMessages(threadId)
			.pipe(take(1))
			.subscribe({
				next: (messages: IChatMessage[]) => {
					this._updateState({ messages, loading: false });
				},
				error: (error: any) => {
					console.error('Error loading messages', error);
					this._updateState({ loading: false });
				}
			});
	}

	public sendMessage(content: string): void {
		const threadId = this._state().activeThreadId;
		if (!threadId) return;

		// Optimistically add user message
		const userMessage: IChatMessage = {
			id: `temp-${Date.now()}`, // temp ID
			threadId,
			content,
			timestamp: new Date(),
			isUser: true,
			isAgent: false,
			status: 'sent'
		};

		this._updateState({
			messages: [...this._state().messages, userMessage],
			loading: true
		});

		this._adapter
			.sendMessage(threadId, content)
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					// Add agent response
					const agentMessage: IChatMessage = {
						id: response.messageId,
						threadId: response.threadId,
						content: response.content,
						timestamp: response.timestamp,
						isUser: false,
						isAgent: true,
						status: 'sent'
					};

					this._updateState({
						messages: [...this._state().messages, agentMessage],
						loading: false
					});

					// Update thread last message
					this._updateThreadLastMessage(response.threadId, response.content, response.timestamp);
				},
				error: (error: any) => {
					console.error('Error sending message', error);

					// Extract error message from response
					let errorMessage = 'Failed to send message. Please try again.';
					if (error?.error?.message) {
						errorMessage = error.error.message;
					} else if (error?.message) {
						errorMessage = error.message;
					}

					// Add error message to UI
					const errorChatMessage: IChatMessage = {
						id: `error-${Date.now()}`,
						threadId,
						content: content,
						timestamp: new Date(),
						isUser: false,
						isAgent: true,
						status: 'error',
						error: errorMessage
					};

					this._updateState({
						messages: [...this._state().messages, errorChatMessage],
						loading: false
					});
				}
			});
	}

	private _updateState(partial: Partial<IChatState>): void {
		this._state.update((state) => ({ ...state, ...partial }));
	}

	private _updateThreadLastMessage(threadId: string, lastMessage: string, lastMessageDate: Date): void {
		const threads = this._state().threads.map((t) =>
			t.id === threadId
				? {
						...t,
						lastMessage,
						lastMessageDate
					}
				: t
		);
		this._updateState({ threads });
	}
}
